/**
 * The per-game save layout table.
 *
 * `savedat.ts` used to be a Fallout 2 codec with the section sizes written into
 * its control flow. It is now a codec for *a* SAVE.DAT, driven by the record
 * below, because Fallout 1 and Fallout 2 write the same header and then diverge
 * in seven places — and a forked copy of a 1300-line parser is a promise to fix
 * every bug twice.
 *
 * Section order and sizes come from the engines' own `master_save_list`:
 *
 *   - Fallout 2: `engine/src/loadsave.cc:236` (`_master_save_list`)
 *   - Fallout 1: `~/f2ce-spike/fallout1-ce/src/game/loadsave.cc:204`
 *     (`master_save_list`)
 *
 *      #  FO2 handler            size     FO1 handler              size
 *      0  _DummyFunc                0     DummyFunc                   0
 *      1  _SaveObjDudeCid           4     SaveObjDudeCid              4
 *      2  scriptsSaveGameGlobalVars 4N    scr_game_save            4N+1
 *      3  _GameMap2Slot           var     GameMap2Slot              var
 *      4  scriptsSaveGameGlobalVars 4N    scr_game_save            4N+1
 *      5  _obj_save_dude          var     obj_save_dude             var
 *      6  critterSave             376     critter_save              372
 *      7  killsSave                76     critter_kill_count_save    60
 *      8  skillsSave               16     skill_save                 16
 *      9  randomSave                0     roll_save                   0
 *     10  perksSave       4*119*party     perk_save                 256
 *     11  combatSave            4|var     combat_save              4|var
 *     12  aiSave        180*packets       combat_ai_save              0
 *     13  statsSave                20     stat_save                  20
 *     14  itemsSave                 0     item_save                   0
 *     15  traitsSave                8     queue_save                var
 *     16  automapSave               4     trait_save                  8
 *     17  preferencesSave          80     automap_save                4
 *     18  characterEditorSave       5     save_options               80
 *     19  wmWorldMap_save         var     editor_save                 5
 *     20  pipboySave              var     save_world_map           1028
 *     21  gameMoviesSave           17     save_pipboy                 0
 *     22  skillsUsageSave         216     gmovie_save                14
 *     23  partyMembersSave        var     skill_use_slot_save       216
 *     24  queueSave               var     partyMemberSave           var
 *     25  interfaceSave             7     intface_save                7
 *     26  _DummyFunc                0     DummyFunc                   0
 *
 * The two structural consequences, and they are the whole reason this file
 * exists rather than a handful of `if (fo1)` branches:
 *
 *   1. **The queue moves.** In Fallout 2 `queueSave` is group 24, deep inside
 *      the opaque tail. In Fallout 1 it is group 15, sitting between the PC
 *      stats and the traits — which are two of the things the editor writes. So
 *      the Fallout 1 parser has to walk the queue to find the traits at all,
 *      where the Fallout 2 parser finds the traits first and locates the queue
 *      afterwards by anchoring on the fixed seven-byte interface group at EOF.
 *   2. **The perk block stops being a search.** Fallout 2 writes
 *      `partySlots * 119` ranks and Fallout 1 writes a flat 64 for the dude
 *      only (`perk_save`, perk.cc:173, over `PERK_COUNT` = 64,
 *      perk_defs.h:71), with no AI-packet section after the combat state
 *      (`combat_ai_save` is `return 0`, combatai.cc:281). The Fallout 1 anchor
 *      therefore has nothing to solve for.
 *
 * Everything not listed as a difference was checked and is the same: the 30051
 * byte header (`SaveHeader`, fallout1-ce loadsave.cc:1699 — same fields, same
 * 224x133 preview, same 128-byte zero trailer), the 32-word dude `Object`
 * (`obj_write_obj`, object.cc:703 + `proto_write_protoUpdateData`, proto.cc),
 * the 20-byte options block with game difficulty first (`save_options`,
 * options.cc:1941), the 5-byte editor block (`editor_save`, editor.cc:5170),
 * and the queue record shape.
 */

/** Which game's SAVE.DAT. Mirrors `config.ts`'s `GameType`; kept local so the
 *  codec does not depend on the shell's runtime configuration. */
export type SaveGameType = 'fo1' | 'fo2';

export type SaveFormat = {
  game: SaveGameType;
  /** Header version bytes, in the order `SaveHeader` writes them. */
  versionMajor: number;
  versionMinor: number;
  versionRelease: string;
  /**
   * Bytes appended to each GVAR block. Fallout 1's `scr_game_save`
   * (scripts.cc:1526) writes `water_movie_play_flag` after the array; Fallout
   * 2's writes nothing.
   */
  gvarTrailerBytes: number;
  /**
   * `CritterProtoData` carries `damageType` in Fallout 2 (proto_types.h) and
   * not in Fallout 1 (proto_types.h:300-308, `critter_write_data`,
   * critter.cc:1021).
   */
  critterHasDamageType: boolean;
  /** `KILL_TYPE_COUNT`: 19 in Fallout 2, 15 in Fallout 1 (proto_types.h:117). */
  killTypeCount: number;
  /** `PERK_COUNT`: 119 / 64 (perk_defs.h:71). */
  perkCount: number;
  /**
   * Fallout 2's `perksSave` writes the dude then every party slot; Fallout 1's
   * `perk_save` writes the dude only, so the block is exactly `perkCount` words.
   */
  perksIncludeParty: boolean;
  /** Fallout 2 writes AI packets after the combat state; Fallout 1 writes none. */
  hasAiPacketSection: boolean;
  /**
   * True when `queue_save` runs *before* `trait_save` (Fallout 1, group 15).
   * False when it is inside the tail (Fallout 2, group 24).
   */
  queueBeforeTraits: boolean;
  /** `TRAIT_COUNT` — 16 in both, but index 13 differs. */
  traitCount: number;
  /**
   * Payload int32s each queue event type writes, indexed by event type. From
   * the engines' `gEventTypeDescriptions` / `q_func` write procs. Fallout 1 has
   * thirteen types (queue.h:10-23); Fallout 2 adds `GSOUND_SFX_EVENT`.
   */
  queueEventDataWords: readonly number[];
  /** `PC_LEVEL_MAX` (stat_defs.h): 99 in Fallout 2, 21 in Fallout 1. */
  pcLevelMax: number;
};

/** `q_func` write-proc payload sizes (fallout1-ce queue.cc:36). */
const FO1_QUEUE_EVENT_DATA_WORDS: readonly number[] = [
  6, // DRUG        item_d_save: stats[3] + modifiers[3]
  0, // KNOCKOUT
  3, // WITHDRAWAL  item_wd_save: field_0, pid, perk
  2, // SCRIPT      script_q_save: sid, fixedParam
  0, // GAME_TIME
  0, // POISON
  2, // RADIATION   critter_save_rads: level, isHealing
  0, // FLARE
  0, // EXPLOSION
  0, // ITEM_TRICKLE
  0, // SNEAK
  0, // EXPLOSION_FAILURE
  0, // MAP_UPDATE_EVENT
];

/** `gEventTypeDescriptions` (engine/src/queue.cc) — Fallout 2 adds one type. */
const FO2_QUEUE_EVENT_DATA_WORDS: readonly number[] = [
  ...FO1_QUEUE_EVENT_DATA_WORDS,
  0, // GSOUND_SFX_EVENT
];

export const SAVE_FORMATS: Readonly<Record<SaveGameType, SaveFormat>> = {
  fo2: {
    game: 'fo2',
    versionMajor: 1,
    versionMinor: 2,
    versionRelease: 'R',
    gvarTrailerBytes: 0,
    critterHasDamageType: true,
    killTypeCount: 19,
    perkCount: 119,
    perksIncludeParty: true,
    hasAiPacketSection: true,
    queueBeforeTraits: false,
    traitCount: 16,
    queueEventDataWords: FO2_QUEUE_EVENT_DATA_WORDS,
    pcLevelMax: 99,
  },
  fo1: {
    game: 'fo1',
    // `VERSION_MAJOR` 1, `VERSION_MINOR` 1 (fallout1-ce version.h:11-13), and
    // `SaveHeader` writes major then minor, exactly as Fallout 2 does.
    versionMajor: 1,
    versionMinor: 1,
    versionRelease: 'R',
    gvarTrailerBytes: 1,
    critterHasDamageType: false,
    killTypeCount: 15,
    perkCount: 64,
    perksIncludeParty: false,
    hasAiPacketSection: false,
    queueBeforeTraits: true,
    traitCount: 16,
    queueEventDataWords: FO1_QUEUE_EVENT_DATA_WORDS,
    pcLevelMax: 21,
  },
};

export const formatFor = (game: SaveGameType): SaveFormat => SAVE_FORMATS[game];

/**
 * Which game wrote this header, from its version bytes alone.
 *
 * The two engines refuse each other's files on exactly this test
 * (`lsgLoadHeaderInSlot` / `LoadHeader`), so it is the engines' own
 * discrimination rather than a heuristic of ours. Anything else returns null and
 * the caller decides — the codec defaults to Fallout 2 and says the version is
 * unsupported, which is what it did before FO1 existed here.
 */
export function gameFromVersion(major: number, minor: number, release: string): SaveGameType | null {
  if (release !== 'R') return null;
  if (major === 1 && minor === 2) return 'fo2';
  if (major === 1 && minor === 1) return 'fo1';
  return null;
}
